import API_CONFIG from '../config';

// Utility function to decode JWT payload safely
const decodeJWTPayload = (token) => {
  try {
    const payloadBase64 = token.split('.')[1];
    if (!payloadBase64) throw new Error('Invalid token format');
    return JSON.parse(atob(payloadBase64));
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

// Check if token is expired or will expire soon (within 2 minutes)
const isTokenExpiringSoon = (token, bufferMinutes = 2) => {
  if (!token) return true;
  
  try {
    const payload = decodeJWTPayload(token);
    if (!payload || !payload.exp) return true;
    
    const currentTime = Math.floor(Date.now() / 1000);
    const bufferTime = bufferMinutes * 60;
    return payload.exp < (currentTime + bufferTime);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

// Clear all authentication tokens from localStorage
const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('authToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userName');
};

// Refresh access token using refresh token
const refreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    console.log('Refreshing access token...');

    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.REFRESH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    const data = await response.json();

    if (response.ok) {
      console.log('Token refresh successful');

      // Store new tokens
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('authToken', data.access_token); // For compatibility

      // Update refresh token if provided
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }

      // Store user info if available
      if (data.user) {
        localStorage.setItem('userId', data.user.id);
        localStorage.setItem('userEmail', data.user.email);
        localStorage.setItem('userName', data.user.name);
      } else {
        // Try to extract user ID from token
        try {
          const payload = decodeJWTPayload(data.access_token);
          if (payload && payload.user_id) {
            localStorage.setItem('userId', payload.user_id);
          }
          if (payload && payload.email) {
            localStorage.setItem('userEmail', payload.email);
          }
        } catch (error) {
          console.error('Error extracting user info from token:', error);
        }
      }

      // Dispatch token refresh event
      window.dispatchEvent(new CustomEvent('tokenRefreshed', { detail: data }));
      
      return data.access_token;
    } else {
      throw new Error(data.message || 'Token refresh failed');
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    clearTokens();
    
    // Dispatch unauthorized event
    window.dispatchEvent(new CustomEvent('unauthorized-error', { detail: error.message }));
    
    throw error;
  }
};

// Enhanced authenticated request function
const makeAuthenticatedRequest = async (url, options = {}) => {
  let accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');

  // Check if access token needs refresh before making the request
  if (isTokenExpiringSoon(accessToken)) {
    console.log('Access token is expiring soon, refreshing proactively...');
    try {
      accessToken = await refreshAccessToken();
    } catch (error) {
      console.error('Proactive token refresh failed:', error);
      // Continue with existing token and let the request handle the error
    }
  }

  // Check if body is FormData to handle file uploads properly
  const isFormData = options.body instanceof FormData;
  
  // Prepare headers with tokens
  const headers = {
    ...options.headers,
  };

  // Only set Content-Type to application/json if it's not FormData
  // For FormData, let the browser set the Content-Type with proper boundary
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  // Add access token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // Add refresh token for backend middleware
  if (refreshToken) {
    headers['x-refresh-token'] = refreshToken;
  }

  try {
    // Make the request
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Check for token refresh in response headers
    try {
      const newAccessToken = response.headers.get('x-new-access-token');
      const newRefreshToken = response.headers.get('x-new-refresh-token');

      if (newAccessToken) {
        console.log('Backend provided new tokens via headers');
        localStorage.setItem('access_token', newAccessToken);
        localStorage.setItem('authToken', newAccessToken);
        
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }
        
        // Dispatch token refresh event
        window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
          detail: { 
            access_token: newAccessToken, 
            refresh_token: newRefreshToken 
          } 
        }));
      }
    } catch (headerError) {
      console.warn('Error reading response headers:', headerError);
      // Continue processing the response even if header reading fails
    }

    // Handle 401 responses
    if (response.status === 401) {
      console.log('Request returned 401, attempting token refresh...');
      
      try {
        const refreshedToken = await refreshAccessToken();
        
        // Retry the request with the new token
        const retryHeaders = {
          ...headers,
          'Authorization': `Bearer ${refreshedToken}`,
        };

        // For FormData requests, make sure Content-Type is not set (browser will set it)
        if (isFormData && retryHeaders['Content-Type']) {
          delete retryHeaders['Content-Type'];
        }

        return fetch(url, {
          ...options,
          headers: retryHeaders,
        });
      } catch (refreshError) {
        console.error('Token refresh failed after 401:', refreshError);
        clearTokens();
        
        // Redirect to login
        window.location.href = '/';
        throw new Error('Authentication failed. Please login again.');
      }
    }

    return response;
  } catch (error) {
    // Handle specific network errors
    if (error.name === 'TypeError' && error.message.includes('CORS')) {
      console.error('CORS error detected:', error);
      throw new Error('Network error: Please check if the server is running and CORS is configured properly.');
    }
    
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      console.error('Network fetch error:', error);
      throw new Error('Network error: Unable to connect to server. Please check your internet connection.');
    }
    
    console.error('Authenticated request failed:', error);
    throw error;
  }
};

// Convenience methods for different HTTP methods
const apiClient = {
  get: (url, options = {}) => makeAuthenticatedRequest(url, { ...options, method: 'GET' }),
  
  post: (url, data, options = {}) => {
    // Handle different data types properly
    let body;
    if (data instanceof FormData) {
      // For FormData (file uploads), use as-is
      body = data;
    } else if (data) {
      // For regular objects, stringify to JSON
      body = JSON.stringify(data);
    }
    
    return makeAuthenticatedRequest(url, {
      ...options,
      method: 'POST',
      body,
    });
  },
  
  put: (url, data, options = {}) => {
    // Handle different data types properly
    let body;
    if (data instanceof FormData) {
      // For FormData (file uploads), use as-is
      body = data;
    } else if (data) {
      // For regular objects, stringify to JSON
      body = JSON.stringify(data);
    }
    
    return makeAuthenticatedRequest(url, {
      ...options,
      method: 'PUT',
      body,
    });
  },
  
  delete: (url, options = {}) => makeAuthenticatedRequest(url, { ...options, method: 'DELETE' }),
  
  // Raw request method for custom use cases
  request: makeAuthenticatedRequest,
};

// Check if user is authenticated
const isAuthenticated = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('authToken');
  return !!token;
};

// Get current user ID
const getCurrentUserId = () => {
  return localStorage.getItem('userId');
};

// Enhanced token validation with refresh attempt
const validateTokenWithRefresh = async () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('authToken');

  if (!token) {
    console.warn('No auth token found');
    return false;
  }

  // Check if token is expired
  try {
    const payload = decodeJWTPayload(token);
    if (!payload || !payload.exp) {
      console.warn('Invalid token format');
      clearTokens();
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    if (payload.exp < currentTime) {
      console.warn('Token expired, attempting refresh...');
      
      try {
        await refreshAccessToken();
        console.log('Token refreshed successfully');
        return true;
      } catch (error) {
        console.error('Token refresh failed:', error);
        clearTokens();
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error validating token:', error);
    clearTokens();
    return false;
  }
};

export {
  apiClient,
  makeAuthenticatedRequest,
  refreshAccessToken,
  isAuthenticated,
  getCurrentUserId,
  validateTokenWithRefresh,
  isTokenExpiringSoon,
  clearTokens,
  decodeJWTPayload,
};

export default apiClient; 